<template>
    <item
        :filler="filler || (!visible && !loaded)"
        force-link
        class="item product-gallery-item"
        :force-lazy-load-image="forceLazyLoadImage"
        :style="itemStyle"
        ref="itemCompInst"
        :data-visible="visible.toString()"
        :data-filler="filler.toString()"
        is-gallery-item
        :registration="registration"
        v-model:item="productItem"
        mobile
        :expected-content-type="expectedContentType"
        :overwrite-action-button-text="
            product != null && product.actionMode === 'add-to-cart'
                ? product.actionButtonText ?? undefined
                : undefined
        "
        :overwrite-action-button="
            product != null && product.actionMode === 'add-to-cart'
        "
        @action-button-click="performAction($event)"
        :action-button-loading="actionLoading"
        :visible="visible"
        :index-of-list="indexOfList"
        style="position: relative"
        @mounted="$emit('mounted', $event)"
        @unmount="$emit('unmount', $event)"
        ><!-- <div
            v-if="intersection != null"
            style="
                position: absolute;
                width: 100%;
                height: 100%;
                background: rgb(0 128 0 / 50%);
                color: white;
                text-shadow: 0px 0px 10px black;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 75px;
            "
            >{{ Math.round(intersection * 100) }}</div
        > --></item
    >
</template>

<style scoped lang="scss">
@import "../../../responsive/style.scss";
.item {
    overflow: hidden;
    list-style: inherit;
    flex-basis: auto;
    flex-grow: 1;
    width: 100%;
    height: min-content;
    box-sizing: border-box;
    margin: 5px;
    text-align: center;
    position: relative;
    display: inline-flex !important;
    flex-direction: column;
    //border: 1px solid #cecece;
    //display:inline-block;
    border-radius: 4px;
    white-space: initial;

    %margin-info {
        padding: 8px;
        box-sizing: border-box;
    }

    .product-image {
        width: 100%;
        background-size: cover;
        background-position: center;
        margin-bottom: 8px;

        ::v-deep img {
            width: 100%;
            height: auto;
        }
    }

    .product-title {
        @extend %margin-info;
        font-weight: 600;
        line-height: 1.2em;
        height: 62px;
        text-overflow: ellipsis;
        overflow: hidden;
        font-size: 12px;
        transition: min-height 400ms ease-out;
    }

    %pricestyle {
        @extend %margin-info;
        display: inline-block;
        width: 100%;
        text-align: center;
    }

    .product-estimate {
        @extend %pricestyle;
        font-size: 14px;
        padding-bottom: 0;
    }

    .product-bid {
        @extend %pricestyle;
        font-size: 20px;
        font-weight: bold;
        padding-top: 0;
    }

    .product-goto-container {
        @extend %margin-info;

        .product-goto {
            border: 2px solid #02b6b5;
            border-radius: 500px;
            background: #02b6b5;
            padding: 15px;
            line-height: 1em;
            font-family: "Open Sans";
            font-size: 14px;
            font-weight: bold;
            color: #000;
            text-align: center;
        }
    }

    .product-cause {
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        position: absolute;
        margin: 8px;
        left: 0px;
        right: 0px;
    }
}
</style>

<script setup lang="ts">
import { type PropType, computed, ref, watch } from "vue";
import item from "../frontend-productlist/product-list/list-item-component/one-per-row/item.vue";
import {
    type ProductListCachedProductViewModel,
    ProductListItem,
    type FillerItem,
} from "../frontend-productlist/product-list/list-item";
import axios from "axios";
import {
    type AddToCartResponse,
    afterAddToCart,
} from "../frontend-productpage/shop/cart-adding";
import { type ProductRegistrationResult } from "../frontend-productlist/product-list/list-item-component/common/product-updater";

const productItem = ref<FillerItem | ProductListItem>({
    filler: true,
} as FillerItem);

const props = defineProps({
    product: Object as PropType<ProductListCachedProductViewModel>,
    width: {
        type: String,
        required: true,
    },
    forceLazyLoadImage: Boolean,
    filler: {
        type: Boolean,
        default: false,
    },
    loaded: Boolean,
    expectedContentType: String,
    visible: {
        type: Boolean,
        default: true,
    },
    registration: Object as PropType<ProductRegistrationResult>,
    indexOfList: Number,
    //intersection: Number,
});

const actionLoading = ref(false);

async function performAction(event: Event) {
    if (actionLoading.value) return;
    actionLoading.value = true;
    try {
        if (props.product == null) throw new Error("no product");
        if (props.product.actionMode === "add-to-cart") {
            if (props.product.actionHref == null)
                throw new Error("missing actionHref");
            const response = await axios.post<AddToCartResponse>(
                props.product.actionHref,
                undefined,
                {
                    params: {
                        productId: props.product.productId,
                        orderQuantity: 1,
                    },
                }
            );
            if (response.data.success !== true) {
                throw new Error(response.data.errors.join("\n"));
            }

            afterAddToCart(
                props.product.productId,
                response.data.cartItemsCount,
                response.data.firstAdd
            );

            return;
        }
        throw new Error("unknown actionMode: " + props.product.actionMode);
    } finally {
        actionLoading.value = false;
    }
}

watch(
    () => ({ filler: props.filler, product: props.product }),
    (newValue) => {
        if (newValue.filler || newValue.product == null) {
            productItem.value = { filler: true } as FillerItem;
            return;
        }
        const product = newValue.product;
        const listItem = new ProductListItem(
            product,
            false,
            product.trackingInfo,
            0
        );
        // if (props.registration) {
        //     listItem.registration = props.registration;
        // } else if (product.registration) {
        //     listItem.registration = product.registration;
        // }
        productItem.value = listItem;
    },
    {
        immediate: true,
        //deep: true,
    }
);

const itemStyle = computed(() => {
    const style: { [key: string]: string } = {};
    style["width"] = props.width;
    return style;
});

const emit = defineEmits(["mounted", "unmount"]);
</script>

<script lang="ts">
export default {
    name: "product-gallery-item",
};
</script>
